import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { TextField, MenuItem, FormLabel, Box } from '@mui/material';

import { FormDatePickerField } from '../form-date-picker-field/form-date-picker-field';

export const DynamicField = ({ fieldDef }: { fieldDef: any }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const watchedValues = useWatch({ control });
  let shouldRender = true;
  if (fieldDef.condition) {
    shouldRender = Object.entries(fieldDef.condition).every(
      ([key, value]) => String(watchedValues?.[key]) === String(value)
    );
  }

  if (!shouldRender) {
    return null;
  }
  const error = errors[fieldDef.field];
  const helperText = error ? String(error.message) : '';
  // A FormLabel so the dialog field styles apply.
  const renderTextField = (field: any, props: any) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        gridColumn: fieldDef.type === 'text' ? '1 / -1' : undefined,
      }}
    >
      <FormLabel required={!!fieldDef.isRequired}>{fieldDef.label}</FormLabel>
      <TextField
        {...field}
        {...props}
        size="small"
        fullWidth
        error={!!error}
        helperText={helperText}
      />
    </Box>
  );

  switch (fieldDef.type) {
    case 'date':
      return (
        <Box sx={{ minWidth: 0 }}>
          <FormDatePickerField
            name={fieldDef.field}
            label={fieldDef.label}
            size="small"
            required={fieldDef.isRequired}
          />
        </Box>
      );

    case 'number': {
      const isDecimal = fieldDef.format === '0.00';
      const props: any = {
        type: 'number',
        inputMode: isDecimal ? 'decimal' : 'numeric',
        step: isDecimal ? '0.01' : '1',
      };

      return (
        <Controller
          name={fieldDef.field}
          control={control}
          render={({ field }) =>
            renderTextField(
              {
                ...field,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  field.onChange(value);
                },
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  let value = e.target.value;
                  if (isDecimal && value !== '') {
                    const num = parseFloat(value);
                    if (!isNaN(num)) {
                      value = num.toFixed(2);
                    }
                  }
                  field.onChange(value);
                  field.onBlur();
                },
                value: field.value ?? '',
              },
              { ...props }
            )
          }
        />
      );
    }

    case 'dropdown':
      return (
        <Controller
          name={fieldDef.field}
          control={control}
          render={({ field }) =>
            renderTextField(field, {
              select: true,
              children: fieldDef.options?.map((opt: any) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              )),
            })
          }
        />
      );
    case 'text':
      return (
        <Controller
          name={fieldDef.field}
          control={control}
          render={({ field }) => renderTextField(field, { type: 'text' })}
        />
      );

    default:
      return;
  }
};
